import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@mafc/trolly';
import { useFeature } from '@mafc/appshell';

import { isNonEmptyArray } from 'utils/helpers.util';
import OrderSummary from 'common/OrderSummary';
import OrderSummaryCTA from 'common/OrderSummary/OrderSummaryCTA';

import { IAppState } from 'config/redux/reducers';
import useStepsProgress from '../../hooks/useStepsProgress.hook';
import EPaymentMethods from '../Payment/PamentMethods.enum';
import useShipments from 'services/shipments/hooks/useShipments.hook';
import useIsExpressCheckout from '../../hooks/useIsExpressCheckout';

import Transform from 'utils/TransformData';
import Tracker from 'utils/Tracker';
import TrackerCheckout from 'utils/Tracker.checkout';
import { CncTrack, CncGTMEvents, formatSlotInfo, getDefaultSlot } from 'pages/Checkout/helpers';
import { getFormattedExpressDeliverySlot } from '../Shipments/helpers/getExpressShipmentEntries.helper';
import { alertMessageKey } from 'pages/Checkout/types/orderError.types';
import { showAlertFromSession } from './CheckoutOrderSummary.util';
import useOrderConfirmation from 'pages/OrderConfirmation/hooks/useOrderConfirmation.hook';
import ExpressButton from 'common/ExpressButton';

import usePaymentService from 'services/payments';
import FEATURES_CONFIG from 'config/features';
import { showPageLoader } from 'common/PageLoader/redux/pageLoader.action';
import PlaceOrderLoadingText from '../SOPForm/PlaceOrderLoadingText';
import useClickAndCollect from '../ShipmentsNew/hooks/useClickAndCollect.hook';
import { DeviceType, EShipmentTabs } from 'pages/Checkout/types/cnc.types';
import COLORS from 'theme/colors';

import DynamicLoader from 'routes/DynamicLoader';
import {
  SubtotalTemplate,
  AgreeTermsTemplate,
  TotalDueTemplate,
} from './snippets/OrderSummarySnippets';
import PriceInUSD from 'common/PriceInUSD';
import { getFormattedPrice } from 'utils/price.util';
import DetailedOrderSummary from 'common/DetailedOrderSummary';
import { PlasticBagPackingInfo } from 'network/response/ICart';
import { prepareShipments } from 'pages/OrderConfirmation/utils/orderConfirmation.transform';
import { IOrderConfirmationPaymentInfo } from 'pages/OrderConfirmation/OrderConfirmation.types';

import useApigeeLabels from 'common/ApigeeLabels/hooks/useApigeeLabels';
import CheckoutSummaryFees from './CheckoutSummaryFees';
import DiscountOrderSummary from 'common/DetailedOrderSummary/DiscountOrderSummary';
import { RoundCornerButton } from 'common/styled/Button.style';
import LoyaltyEarnedPoints from '../LoyaltyEarnedPoints';
import CreateOrderError from './CreateOrderError/CreateOrderError';
import { EPaymentArchitectureTypes } from 'pages/Checkout/redux/actions/paymentArchitecture.action';
import { extractBinFromCard } from '../Payment/helpers/payment.helpers';
import { FormType } from 'services/payments/core/PaymentsManagerSDK';
import { newRelicCustomEvent } from 'utils/newRelicTracker';
import { CurrencyCodes, NEW_RELIC_EVENT } from 'pages/Checkout/constants/checkout.constant';
import { HelpersUtil } from '@mafc/common';
import CheckoutTotalSavings from './CheckoutTotalSavings';

const ViewDetailsDrawer = DynamicLoader(() =>
  import(
    /* webpackChunkName: "ViewDetailsDrawerChunk" */
    './ViewDetailsDrawer'
  )
) as any;

const SOPForm = DynamicLoader(() =>
  import(
    /* webpackChunkName: "SOPFormChunk" */
    '../SOPForm'
  )
) as any;

interface Props {
  isLoading: boolean;
  Promo?: React.ReactNode;
  nicField: {
    isRequired: boolean;
    hasError: boolean;
    errorReason: string;
    value: string;
  };
}

const CheckoutOrderSummary: React.FC<Props> = ({ isLoading, Promo, nicField }) => {
  const dispatch = useDispatch();
  const isExpressCheckout = useIsExpressCheckout();
  const isCheckoutDotComSupported = useFeature('checkout-dot-com-supported').on;

  const {
    deliverySlot,
    cart: { cartData },
    checkout: {
      orderDetails: { orderFieldMap, cybersourceURL },
      payment: paymentState,
      nonCardRedirectionURI,
      paymentArchitecture,
    },
    appConfig: { areaCode, latitude, longitude },
    orderSummary: { orderTotalCurrency },
  } = useSelector((state: IAppState) => state);
  const isUSDSelected = orderTotalCurrency === CurrencyCodes.USD;
  const { initPayment } = useSelector((state: IAppState) => state.paymentService);
  const [isViewDetailsOpen, toggleViewDetailDrawer] = useState(false);
  const { createOrderAction, checkCardValidation, createUpdateOrderAction } = usePaymentService();
  const isSavedCardEnabled = useFeature('checkout.saved_cards.enabled').on;
  const [{ deliverTo, shipments, payment }] = useStepsProgress();
  const {
    express,
    hasClickAndCollect,
    clickAndCollect,
    shipments: orderShipments,
  } = useShipments();
  const { captureCheckoutDetails } = useOrderConfirmation();
  const { clickAndCollectState } = useClickAndCollect();
  const [termsSelected, toggleTermsCondition] = useState(true);

  useEffect(captureCheckoutDetails);

  const placeOrderCallback = () => {
    const {
      paymentMode: { type },
      paymentDetails,
    } = paymentState;
    const params = {
      paymentInfoId: '',
      paymentModeSelected: type,
      saveTheCard: false,
      nicNumber: nicField.isRequired ? nicField.value : '',
      areaCode,
      accountEmailId: '',
      accountNumber: '',
      isCheckoutDotComSupported,
      isCheckoutDotComFlow: false,
    };

    if (type === EPaymentMethods.CREDIT_CARD) {
      if (paymentDetails.id) {
        // Saved cards action
        params.paymentInfoId = paymentDetails.id;
      } else {
        // New card action
        params.saveTheCard = false;
        // paymentDetails.savePaymentInfo
        //   ? paymentDetails.savePaymentInfo
        //   : false;
      }
      // Passing flag only for credit card payment method as modifying the order response on its basis
      params.isCheckoutDotComFlow = isCheckoutDotComSupported;
    }

    if (type === EPaymentMethods.MOBILE_WALLET) {
      const {
        easyPaisaDetails: { emailAddress, mobileNumber },
      } = paymentDetails;
      params.accountEmailId = emailAddress;
      params.accountNumber = mobileNumber;
    }

    CtaGTMEvents();
    dispatch(showPageLoader(<PlaceOrderLoadingText />));
    createOrderAction({ params, hasClickAndCollect });
  };

  const masterCardPlaceOrderCallback = () => {
    checkCardValidation(({ isValid }) => {
      if (isValid) {
        placeOrderCallback();
      }
    });
  };

  const placeOrderV3Callback = () => {
    const {
      paymentMode: { type },
      paymentDetails: { newCardDetails, id: cardId, bin, cvvValue },
    } = paymentState;
    const params: any = {
      paymentMode: type,
      isSavedCard: isSavedCardEnabled,
      latitude,
      longitude,
      ...(nicField.isRequired && nicField.value && { nicNumber: nicField.value }),
      isCheckoutDotComSupported,
      ...(newCardDetails && Object.keys(newCardDetails).length && { info: newCardDetails }),
      ...(cardId && { info: { cardId, cvv: cvvValue } }),
      is_usd_selected: isUSDSelected,
    };
    if (type === EPaymentMethods.CREDIT_CARD) {
      let binCode = '';
      if (isSavedCardEnabled && cardId) {
        binCode = bin;
      } else {
        binCode = extractBinFromCard(paymentState.paymentDetails.newCardDetails.card_number);
      }
      params.binCode = binCode;
    }
    CtaGTMEvents();
    dispatch(showPageLoader(<PlaceOrderLoadingText />));
    createUpdateOrderAction({ params });
  };

  const addLbpToUsdInSession = () => {
    if (isUSDSelected) {
      sessionStorage.setItem('changeCurrencyToUSD', 'true');
    }
  };

  const getPlaceOrderCallback = () => {
    addLbpToUsdInSession();

    const {
      paymentMode: { type },
    } = paymentState;
    const isMobile = HelpersUtil.isMobileDevice(navigator.userAgent);
    newRelicCustomEvent(NEW_RELIC_EVENT.PLACE_ORDER_CLICKED, {
      paymentArchitecture: paymentArchitecture.pv,
      device: isMobile ? DeviceType.MWEB : DeviceType.DESKTOP,
    });
    if (paymentArchitecture.pv) {
      if (paymentArchitecture.pv === EPaymentArchitectureTypes.V3) {
        placeOrderV3Callback();
        return;
      }
    }
    FEATURES_CONFIG.settings.enableMasterCard &&
    !isCheckoutDotComSupported &&
    type === EPaymentMethods.CREDIT_CARD
      ? masterCardPlaceOrderCallback()
      : placeOrderCallback();
  };
  useEffect(() => {
    if (nonCardRedirectionURI) {
      window.location.href = nonCardRedirectionURI;
    }
  }, [nonCardRedirectionURI]);

  const isCardSaved =
    paymentState && paymentState.paymentDetails && paymentState.paymentDetails.id
      ? !!paymentState.paymentDetails.id
      : false;

  const CtaGTMEvents = useCallback(() => {
    const { paymentMode, code, store, expressCount } = cartData;
    const {
      paymentMode: { type },
    } = paymentState;
    // Checkout progress event
    try {
      Tracker.checkoutProgress(Transform.productData(cartData), paymentMode, 2, code, store);
      Tracker.gtmPlaceOrderCta('place_order_cta_click', isCardSaved, {
        place_order_cta_click: '1',
      });
      TrackerCheckout.setPurchaseEventSession();

      const transformedItems = Transform.productData(cartData, undefined, undefined, { ga4: true });
      TrackerCheckout.addPaymentInfo(transformedItems, {
        cartValue: cartData.subTotal.value,
        paymentType: type,
      });
    } catch (error) {
      // Data error
      // tslint:disable: no-console
      console.error(error);
    }

    // These events will fire if express is shown on the page
    if (expressCount && expressCount > 0) {
      const expressEnable = isNonEmptyArray(express.entries);

      if (expressEnable) {
        TrackerCheckout.CtaClickedWithExpressLoaded(
          getFormattedExpressDeliverySlot(express.deliverySlot),
          true
        );
      } else {
        TrackerCheckout.CtaClickedWithExpressLoaded('', false);
      }
    }
    if (hasClickAndCollect) {
      const {
        store: s = { name: '', displayName: '' },
        deliverySlot: d = { endTime: '', beginTime: '', deliveryDate: '' },
      } = clickAndCollect;

      CncTrack(CncGTMEvents.cnc_order, {
        cc_store_id: s.name,
        cc_store_name: s.displayName,
        cc_selected_timeslot: formatSlotInfo(d),
        cc_default_timeslot: getDefaultSlot(),
      });
    }
  }, [cartData, express, hasClickAndCollect, clickAndCollect, isCardSaved, paymentState]);

  if (!deliverySlot.loading) {
    // show alert if coming from delivery slot expiry error while placing order
    showAlertFromSession(alertMessageKey);
  }

  const isAPILoading = useSelector((state: any) => {
    const {
      checkout: {
        delivery,
        shipment: { express: expressLoadingState },
        payment: paymentLoadingState,
      },
    } = state;
    return (
      delivery.loading ||
      expressLoadingState.loading ||
      paymentLoadingState.loading ||
      isLoading ||
      false
    );
  });

  let isValidSavedCard = true;
  if (
    isValidSavedCard &&
    paymentState.paymentDetails.isCVVRequired &&
    cartData.paymentMode !== EPaymentMethods.COD
  ) {
    isValidSavedCard = paymentState.paymentDetails.isValid;
  }
  const isCODWithMultiShipments =
    cartData.shipments &&
    cartData.shipments.length > 1 &&
    (cartData.paymentMode === EPaymentMethods.COD ||
      cartData.paymentMode === EPaymentMethods.CARD_ON_DELIVERY);

  const disabled =
    isAPILoading ||
    !termsSelected ||
    !(deliverTo && shipments && payment) ||
    (nicField.isRequired && (nicField.hasError || nicField.value.length < 1)) ||
    (clickAndCollectState.activeTab === EShipmentTabs.CNC_TAB &&
      clickAndCollectState.loading === true) ||
    isCODWithMultiShipments ||
    !isValidSavedCard;
  const { t } = useTranslation();

  useEffect(() => {
    if (!disabled) {
      Tracker.gtmPlaceOrderCta('place_order_cta_active', isCardSaved, {
        place_order_cta_active: '1',
      });
    }
  }, [disabled, isCardSaved]);

  const apigeeLabels = useApigeeLabels();

  const shipmentsData = prepareShipments(orderShipments || [], apigeeLabels);
  const { sustainabilityConsent = null } = cartData;

  return (
    <>
      <OrderSummary isLoading={isLoading} Promo={Promo} bg="none" paddingStyle={[0]}>
        {(
          {
            subTotal,
            totalPriceWithTax,
            paymentMode,
            loyaltyPoints,
            codFee,
            loyaltyVouchers = [],
            appliedVouchers = [],
            totalFees,
            totalAppliedDiscounts,
            totalSavings,
            totalBundleDiscount,
          },
          stickyTotalEl,
          plasticBagPackingInfo = {} as PlasticBagPackingInfo
        ) => {
          return (
            <>
              {!!totalSavings && <CheckoutTotalSavings totalSavings={totalSavings} />}
              {!!loyaltyPoints && <LoyaltyEarnedPoints earnedPoints={loyaltyPoints} />}
              <Box
                bg={COLORS.WHITE}
                p={[2, 3]}
                mb={3}
                sx={{
                  borderRadius: 3,
                }}
              >
                <SubtotalTemplate priceDetails={subTotal} />

                <CheckoutSummaryFees
                  plasticBagPackingInfo={plasticBagPackingInfo}
                  sustainabilityConsent={sustainabilityConsent}
                />

                <DiscountOrderSummary
                  appliedVouchers={appliedVouchers}
                  loyaltyVouchers={loyaltyVouchers}
                  totalBundleDiscount={totalBundleDiscount}
                  totalAppliedDiscounts={totalAppliedDiscounts}
                  showHeader
                />

                <TotalDueTemplate priceDetails={totalPriceWithTax} />

                <PriceInUSD price={totalPriceWithTax} variant="checkoutOrderSummary" />

                <RoundCornerButton
                  data-testid="__order_summary_view_details_btn__"
                  onClick={toggleViewDetailDrawer.bind(null, true)}
                  variant="custom"
                  preventTextTransform
                >
                  {t('orderSummary.viewDetails')}
                </RoundCornerButton>
              </Box>

              <Box mx={[2, 0]}>
                <AgreeTermsTemplate
                  toggleTermsCondition={toggleTermsCondition}
                  termsSelected={termsSelected}
                />
                {/* Totals + proceed button */}
                <OrderSummaryCTA
                  parentRef={stickyTotalEl}
                  heading={''}
                  subHeading={''}
                  addOnInfo={''}
                  ctaText={t('placeOrder')}
                  price={getFormattedPrice(totalPriceWithTax, false, true, true)}
                  totalPrice={totalPriceWithTax}
                  disabled={disabled}
                  callback={getPlaceOrderCallback}
                  showPrimaryButton={!isExpressCheckout}
                  expressBtn={
                    isExpressCheckout && (
                      <ExpressButton
                        ctaText={t('placeOrder')}
                        price={getFormattedPrice(totalPriceWithTax, false, true, true)}
                        isSmallButton={false}
                        callback={getPlaceOrderCallback}
                        disabled={disabled}
                        variant="primary"
                        showExpressIcon={false}
                      />
                    )
                  }
                />
              </Box>
              {isViewDetailsOpen && (
                <ViewDetailsDrawer
                  isOpen={isViewDetailsOpen}
                  onClose={toggleViewDetailDrawer.bind(null, false)}
                >
                  <DetailedOrderSummary
                    subTotal={subTotal}
                    shipments={shipmentsData}
                    codFee={codFee}
                    paymentMode={paymentMode}
                    totalFees={totalFees}
                    totalAppliedDiscounts={totalAppliedDiscounts}
                    totalBundleDiscount={totalBundleDiscount}
                    appliedVouchers={appliedVouchers}
                    loyaltyVouchers={loyaltyVouchers}
                    totalPriceWithTax={totalPriceWithTax}
                    paymentInfo={
                      {
                        cardType: {
                          code: paymentMode,
                        },
                      } as IOrderConfirmationPaymentInfo
                    }
                    sustainabilityConsent={sustainabilityConsent}
                    plasticBagPackingInfo={plasticBagPackingInfo}
                  />
                </ViewDetailsDrawer>
              )}
              {paymentState.paymentMode.type !== EPaymentMethods.MOBILE_WALLET && (
                <SOPForm orderDetails={orderFieldMap} cybersourceURL={cybersourceURL} />
              )}
              {initPayment.action === FormType.FORM && (
                <SOPForm orderDetails={initPayment.params} cybersourceURL={initPayment.url} />
              )}
              <CreateOrderError />
            </>
          );
        }}
      </OrderSummary>
    </>
  );
};

export default CheckoutOrderSummary;
